import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";

import type { NodeDefaultsResponse, InternalRequestDefaults } from "@/services/enterpriseCatalogService";

import type { LegalDocumentType } from "@/components/features/notarial-requests/legal-entity-request-fields";
import {
  EMPTY_MANUAL_COMPANY_FIELDS,
  type ManualCompanyFieldsState,
} from "@/utils/manualCompanySnapshot";
import { DEFAULT_CERTIFICATE_FULFILLMENT_MODES_ALLOWED } from "@/constants/certificateFulfillmentModes";
import type { ValidateRutResponse } from "@/services/authService";
import type { IncomeCertificateWorkModality } from "@/services/userNotarialRequestsService";

/** Must stay in sync with `ACTIVITY_KEYS` in `income-certificate-wizard`. */
export type IncomeActivityKey =
  | "lawyer"
  | "architect"
  | "merchant"
  | "accountant"
  | "engineer"
  | "retired"
  | "doctor"
  | "other";

/** Must match `PRESENTING_ENTITY_KEYS` in `income-certificate-wizard`. */
export type IncomePresentingEntityPreset =
  | "bank"
  | "landlord_or_real_estate"
  | "consulate"
  | "employer"
  | "university"
  | "government"
  | "lender"
  | "other";

export interface IncomeCertificateWizardFormState {
  fulfillmentMode: string;
  setFulfillmentMode: React.Dispatch<React.SetStateAction<string>>;
  periodMonths: 6 | 12 | 24;
  setPeriodMonths: React.Dispatch<React.SetStateAction<6 | 12 | 24>>;
  presentingEntityKey: IncomePresentingEntityPreset | "";
  setPresentingEntityKey: React.Dispatch<React.SetStateAction<IncomePresentingEntityPreset | "">>;
  presentingEntityOther: string;
  setPresentingEntityOther: React.Dispatch<React.SetStateAction<string>>;
  presentingInstitution: string;
  setPresentingInstitution: React.Dispatch<React.SetStateAction<string>>;
  presentingInstitutionOther: string;
  setPresentingInstitutionOther: React.Dispatch<React.SetStateAction<string>>;
  activity: IncomeActivityKey | "";
  setActivity: React.Dispatch<React.SetStateAction<IncomeActivityKey | "">>;
  activityCustom: string;
  setActivityCustom: React.Dispatch<React.SetStateAction<string>>;
  specialFormatRequested: boolean;
  setSpecialFormatRequested: React.Dispatch<React.SetStateAction<boolean>>;
  specialFormatFile: File | null;
  setSpecialFormatFile: React.Dispatch<React.SetStateAction<File | null>>;
  workModality: IncomeCertificateWorkModality | "";
  setWorkModality: React.Dispatch<React.SetStateAction<IncomeCertificateWorkModality | "">>;
  salaryReceipts: (File | null)[];
  setSalaryReceipts: React.Dispatch<React.SetStateAction<(File | null)[]>>;
  companyRut: string;
  setCompanyRut: React.Dispatch<React.SetStateAction<string>>;
  validatedCompany: ValidateRutResponse | null;
  setValidatedCompany: React.Dispatch<React.SetStateAction<ValidateRutResponse | null>>;
  companyRutManualMode: boolean;
  setCompanyRutManualMode: React.Dispatch<React.SetStateAction<boolean>>;
  incomeManualCompanyFields: ManualCompanyFieldsState;
  setIncomeManualCompanyFields: Dispatch<SetStateAction<ManualCompanyFieldsState>>;
  updateIncomeManualCompanyField: (field: keyof ManualCompanyFieldsState, value: string) => void;
  isValidatingRut: boolean;
  setIsValidatingRut: React.Dispatch<React.SetStateAction<boolean>>;
  dgiFile: File | null;
  setDgiFile: React.Dispatch<React.SetStateAction<File | null>>;
  tarjetaRutFile: File | null;
  setTarjetaRutFile: React.Dispatch<React.SetStateAction<File | null>>;
  form6906File: File | null;
  setForm6906File: React.Dispatch<React.SetStateAction<File | null>>;
  bpsNumber: string;
  setBpsNumber: React.Dispatch<React.SetStateAction<string>>;
  bpsFile: File | null;
  setBpsFile: React.Dispatch<React.SetStateAction<File | null>>;
  invoices: (File | null)[];
  setInvoices: React.Dispatch<React.SetStateAction<(File | null)[]>>;
  cajaFile: File | null;
  setCajaFile: React.Dispatch<React.SetStateAction<File | null>>;
  legalDocumentType: LegalDocumentType | "";
  setLegalDocumentType: React.Dispatch<React.SetStateAction<LegalDocumentType | "">>;
  legalDocumentFrontFile: File | null;
  setLegalDocumentFrontFile: React.Dispatch<React.SetStateAction<File | null>>;
  legalDocumentBackFile: File | null;
  setLegalDocumentBackFile: React.Dispatch<React.SetStateAction<File | null>>;
  passportDocumentFile: File | null;
  setPassportDocumentFile: React.Dispatch<React.SetStateAction<File | null>>;
  handleLegalDocumentTypeChange: (value: LegalDocumentType) => void;
  enterpriseDefaults: InternalRequestDefaults | null;
  enterpriseName: string;
  lockedFields: Set<string>;
  reset: () => void;
}

/**
 * Holds all income certificate wizard field state in the parent flow so it survives
 * `IncomeCertificateWizard` remounts (e.g. React Strict Mode).
 */
export function useIncomeCertificateWizardFormState(
  allowedFulfillmentModes: string[],
  nodeDefaultsResponse?: NodeDefaultsResponse | null,
): IncomeCertificateWizardFormState {
  const modes =
    allowedFulfillmentModes.length > 0 ? allowedFulfillmentModes : [...DEFAULT_CERTIFICATE_FULFILLMENT_MODES_ALLOWED];

  const [fulfillmentMode, setFulfillmentMode] = useState<string>("");
  const [periodMonths, setPeriodMonths] = useState<6 | 12 | 24>(12);
  const [presentingEntityKey, setPresentingEntityKey] = useState<IncomePresentingEntityPreset | "">("");
  const [presentingEntityOther, setPresentingEntityOther] = useState("");
  const [presentingInstitution, setPresentingInstitution] = useState("");
  const [presentingInstitutionOther, setPresentingInstitutionOther] = useState("");
  const [activity, setActivity] = useState<IncomeActivityKey | "">("");
  const [activityCustom, setActivityCustom] = useState("");
  const [specialFormatRequested, setSpecialFormatRequested] = useState(false);
  const [specialFormatFile, setSpecialFormatFile] = useState<File | null>(null);
  const [workModality, setWorkModality] = useState<IncomeCertificateWorkModality | "">("");
  const [salaryReceipts, setSalaryReceipts] = useState<(File | null)[]>(() =>
    Array.from({ length: 12 }, () => null),
  );
  const [companyRut, setCompanyRut] = useState("");
  const [validatedCompany, setValidatedCompany] = useState<ValidateRutResponse | null>(null);
  const [companyRutManualMode, setCompanyRutManualMode] = useState(false);
  const [incomeManualCompanyFields, setIncomeManualCompanyFields] =
    useState<ManualCompanyFieldsState>(EMPTY_MANUAL_COMPANY_FIELDS);
  const [isValidatingRut, setIsValidatingRut] = useState(false);

  const updateIncomeManualCompanyField = useCallback(
    (field: keyof ManualCompanyFieldsState, value: string): void => {
      setIncomeManualCompanyFields((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );
  const [dgiFile, setDgiFile] = useState<File | null>(null);
  const [tarjetaRutFile, setTarjetaRutFile] = useState<File | null>(null);
  const [form6906File, setForm6906File] = useState<File | null>(null);
  const [bpsNumber, setBpsNumber] = useState("");
  const [bpsFile, setBpsFile] = useState<File | null>(null);
  const [invoices, setInvoices] = useState<(File | null)[]>(() =>
    Array.from({ length: 12 }, () => null),
  );
  const [cajaFile, setCajaFile] = useState<File | null>(null);
  const [legalDocumentType, setLegalDocumentType] = useState<LegalDocumentType | "">("");
  const [legalDocumentFrontFile, setLegalDocumentFrontFile] = useState<File | null>(null);
  const [legalDocumentBackFile, setLegalDocumentBackFile] = useState<File | null>(null);
  const [passportDocumentFile, setPassportDocumentFile] = useState<File | null>(null);

  const enterpriseDefaults = nodeDefaultsResponse?.defaults ?? null;
  const enterpriseName = nodeDefaultsResponse?.enterpriseName ?? '';

  const lockedFields = useMemo(() => {
    const locked = new Set<string>();
    if (!enterpriseDefaults) return locked;
    if (enterpriseDefaults.fulfillmentMode) locked.add('fulfillmentMode');
    if (enterpriseDefaults.periodMonths) locked.add('periodMonths');
    if (enterpriseDefaults.presentingEntityAsEnterprise) {
      locked.add('presentingEntityKey');
      locked.add('presentingEntityOther');
    }
    if (enterpriseDefaults.specialFormatTemplateUrl) {
      locked.add('specialFormatRequested');
      locked.add('specialFormatFile');
    }
    return locked;
  }, [enterpriseDefaults]);

  // Apply enterprise defaults on first load
  useEffect(() => {
    if (!enterpriseDefaults) return;
    if (enterpriseDefaults.fulfillmentMode) {
      setFulfillmentMode(enterpriseDefaults.fulfillmentMode);
    }
    if (enterpriseDefaults.periodMonths) {
      setPeriodMonths(enterpriseDefaults.periodMonths);
    }
    if (enterpriseDefaults.presentingEntityAsEnterprise && nodeDefaultsResponse?.enterpriseName) {
      setPresentingEntityKey('other');
      setPresentingEntityOther(nodeDefaultsResponse.enterpriseName);
    }
    if (enterpriseDefaults.specialFormatTemplateUrl) {
      setSpecialFormatRequested(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFulfillmentMode((prev) => {
      if (prev !== "" && modes.includes(prev)) {
        return prev;
      }
      return modes[0] ?? "physical_only";
    });
  }, [modes]);

  const handleLegalDocumentTypeChange = useCallback((value: LegalDocumentType): void => {
    setLegalDocumentType(value);
    if (value === "national_id") {
      setPassportDocumentFile(null);
    } else {
      setLegalDocumentFrontFile(null);
      setLegalDocumentBackFile(null);
    }
  }, []);

  useEffect(() => {
    if (workModality === "") return;
    setSalaryReceipts(Array.from({ length: periodMonths }, () => null));
    setInvoices(Array.from({ length: periodMonths }, () => null));
  }, [workModality, periodMonths]);

  const reset = useCallback((): void => {
    setFulfillmentMode("");
    setPeriodMonths(12);
    setPresentingEntityKey("");
    setPresentingEntityOther("");
    setPresentingInstitution("");
    setPresentingInstitutionOther("");
    setActivity("");
    setActivityCustom("");
    setSpecialFormatRequested(false);
    setSpecialFormatFile(null);
    setWorkModality("");
    setSalaryReceipts(Array.from({ length: 12 }, () => null));
    setCompanyRut("");
    setValidatedCompany(null);
    setCompanyRutManualMode(false);
    setIncomeManualCompanyFields(EMPTY_MANUAL_COMPANY_FIELDS);
    setIsValidatingRut(false);
    setDgiFile(null);
    setTarjetaRutFile(null);
    setBpsNumber("");
    setBpsFile(null);
    setInvoices(Array.from({ length: 12 }, () => null));
    setCajaFile(null);
    setLegalDocumentType("");
    setLegalDocumentFrontFile(null);
    setLegalDocumentBackFile(null);
    setPassportDocumentFile(null);
  }, []);

  return {
    fulfillmentMode,
    setFulfillmentMode,
    periodMonths,
    setPeriodMonths,
    presentingEntityKey,
    setPresentingEntityKey,
    presentingEntityOther,
    setPresentingEntityOther,
    presentingInstitution,
    setPresentingInstitution,
    presentingInstitutionOther,
    setPresentingInstitutionOther,
    activity,
    setActivity,
    activityCustom,
    setActivityCustom,
    specialFormatRequested,
    setSpecialFormatRequested,
    specialFormatFile,
    setSpecialFormatFile,
    workModality,
    setWorkModality,
    salaryReceipts,
    setSalaryReceipts,
    companyRut,
    setCompanyRut,
    validatedCompany,
    setValidatedCompany,
    companyRutManualMode,
    setCompanyRutManualMode,
    incomeManualCompanyFields,
    setIncomeManualCompanyFields,
    updateIncomeManualCompanyField,
    isValidatingRut,
    setIsValidatingRut,
    dgiFile,
    setDgiFile,
    tarjetaRutFile,
    setTarjetaRutFile,
    form6906File,
    setForm6906File,
    bpsNumber,
    setBpsNumber,
    bpsFile,
    setBpsFile,
    invoices,
    setInvoices,
    cajaFile,
    setCajaFile,
    legalDocumentType,
    setLegalDocumentType,
    legalDocumentFrontFile,
    setLegalDocumentFrontFile,
    legalDocumentBackFile,
    setLegalDocumentBackFile,
    passportDocumentFile,
    setPassportDocumentFile,
    handleLegalDocumentTypeChange,
    enterpriseDefaults,
    enterpriseName,
    lockedFields,
    reset,
  };
}
